<script lang="ts" setup>
import { onMounted, provide, ref, watch } from 'vue'
import { useAuthStore } from '@/modules/auth/stores/authStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useUserStore } from '@/modules/user/stores/userStore'

import { useSettingsStore } from '@/modules/settings/store/settingsStore'
// Стор API — загрузка серверов
import { useApiStore } from '@/modules/api/store/apiStore'
import { usePayStore } from '@/modules/pay/store/payStore'
import { useRouter } from 'vue-router'

import { ERouteNames } from '@/router/ERouteNames'
// Утилита определения Telegram-контекста
import { isTelegram } from '@/utils/isTelegram'
import { awaitTelegramWebApp } from '@/utils/awaitTelegramWebApp'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import PromoInterface from '@/components/promo-interface/PromoInterface.vue'
import PaidInterface from '@/views/PaidInterface.vue'
import GuestInterface from '@/views/GuestInterface.vue'
import AppFooter from '@/components/AppFooter.vue'
import ServiceWorks from '@/components/ServiceWorks.vue'
// Диалог привязки email — открывается после логина, если email не привязан
import EmailBindDialog from '@/modules/user/components/EmailBindDialog.vue'
// Онбординг-тур для новых пользователей
import OnboardingOverlay from '@/components/onboarding/OnboardingOverlay.vue'

// Определяем мобильный экран для адаптивных отступов
const { mobile } = useDisplay()
const { t } = useI18n()

const authStore = useAuthStore()
const feedbackStore = useFeedbackStore()
const userStore = useUserStore()
const settingsStore = useSettingsStore()
const apiStore = useApiStore()
const payStore = usePayStore()
const router = useRouter()

// Флаг видимости диалога привязки email
const showEmailBindDialog = ref(false)
// Флаг видимости онбординг-тура
const showOnboarding = ref(false)

// Предоставляем функцию запуска онбординга дочерним компонентам (InstructionsButton)
provide('startOnboarding', () => {
  showOnboarding.value = true
})

// Проверка наличия токенов в localStorage
const hasTokens = !!(localStorage.getItem('vpn_access_token') || localStorage.getItem('vpn_refresh_token'))

// Запускаем инициализацию, только если есть токены или мы в Telegram
const isInitializing = ref(hasTokens || isTelegram())

const handleUser = async (): Promise<void> => {
  console.log('handleUser запущен')
  if (isInitializing.value) {
    feedbackStore.isGlobalLoading = true
  }
  try {
    // В TG ждём onload telegram-web-app.js (Promise из index.html), иначе initData ещё пустой
    if (isTelegram()) {
      await awaitTelegramWebApp()
    }

    // 1. Проверяем Telegram-контекст
    const initData = window.Telegram?.WebApp?.initData

    if (initData && isTelegram()) {
      // Telegram-авторизация: restoreAuth или goLogin
      const restored = await authStore.restoreAuth()
      if (!restored) {
        const loginResult = await authStore.goLogin({ initData })
        if (!loginResult.ok) {
          feedbackStore.showToast({
            type: 'error',
            message: loginResult.error || t('errors.telegramLoginFailed'),
          })
          await router.push({ name: ERouteNames.NOT_FOUND })
          return
        }
      }
    } else {
      // Web-авторизация: пробуем восстановить сессию по сохранённому токену
      const restored = await authStore.restoreAuth()
      if (!restored) {
        // Гость в вебе — редиректа на /auth больше нет, показываем тарифы на главной.
        // Настройки нужны для баннера ServiceWorks. Сами тарифы грузит TariffsInterface.onMounted.
        await settingsStore.getSettings()
        return
      }
    }

    // 2. После auth — грузим settings, userData и серверы параллельно
    const [userDataLoaded] = await Promise.all([
      userStore.getUserData(),
      settingsStore.getSettings(),
      apiStore.loadServers(),
      payStore.loadSavedPaymentMethods(false),
    ])

    if (userDataLoaded && !isTelegram()) {
      const paymentStarted = await payStore.tryStartPendingPayment()
      if (paymentStarted) {
        await router.replace({ name: ERouteNames.PAYMENT })
        return
      }
    }


    // 4. Проверяем привязку email — если нет, открываем диалог привязки (закрываемый, но при следующем заходе откроется снова)
    const hasEmail = !!userStore.user?.email
    if (!hasEmail) {
      showEmailBindDialog.value = true
    }

    // 5. Если userData не загрузился — пробуем re-login (только для Telegram) или logout
    if (!userDataLoaded) {
      console.warn('handleUser getUserData не удался')
      if (initData && isTelegram()) {
        const reLoginResult = await authStore.goLogin({ initData })
        if (reLoginResult.ok) {
          const retryUserData = await userStore.getUserData()
          if (retryUserData) return
        } else {
          feedbackStore.showToast({
            type: 'error',
            message: reLoginResult.error || t('errors.telegramLoginFailed'),
          })
        }
      }
      await authStore.logout('Не удалось загрузить данные пользователя в LandingPage (getUserData)')
      await router.push({ name: isTelegram() ? ERouteNames.NOT_FOUND : ERouteNames.AUTH })
    }
  } finally {
    isInitializing.value = false
    feedbackStore.isGlobalLoading = false
  }
}


onMounted(async () => {
  await handleUser()

  // 6. Триггер онбординга: онбординг на сервере не завершен + активен тариф
  const shouldOnboard =
    userStore.user?.isTariffActive && !userStore.user?.isOnboardingCompleted

  if (shouldOnboard) {
    if (showEmailBindDialog.value) {
      // Ждём закрытия email-диалога → потом запускаем онбординг
      const unwatch = watch(showEmailBindDialog, (val) => {
        if (!val) {
          // Задержка для анимации закрытия диалога
          setTimeout(() => {
            showOnboarding.value = true
          }, 500)
          unwatch()
        }
      })
    } else {
      // Email уже привязан — запускаем онбординг сразу (с задержкой для DOM)
      setTimeout(() => {
        showOnboarding.value = true
      }, 500)
    }
  }
})
</script>

<template>
  <div class="d-flex flex-column align-center flex-grow-1 w-100">
    <v-container fluid class="d-flex flex-column align-center ga-6 flex-grow-1 w-100" :class="mobile ? 'pa-4' : 'py-4 px-0'">
      <service-works v-if="settingsStore.showGlobalNotification && settingsStore.notificationText" />

      <template v-if="!isInitializing">
        <!-- Авторизованный с активным (или недавно закончившимся) тарифом — показываем PaidInterface -->
        <PaidInterface
          v-if="authStore.isAuth && userStore.isUserData && (userStore.user?.isTariffActive || userStore.user?.lastTariffExpiresAt)"
        />
        <!-- Во всех остальных случаях (авторизованный без тарифа, гость) — GuestInterface с тарифами -->
        <GuestInterface v-else />

        <!-- Рефералка скрыта для неавторизованных — статистика без userStore.user не имеет смысла -->
        <promo-interface v-if="authStore.isAuth" />

        <!-- Диалог привязки email — открывается после логина, если email не привязан. Закрываемый: пользователь может скипнуть, но при следующем входе откроется снова. -->
        <email-bind-dialog
          v-if="authStore.isAuth"
          v-model="showEmailBindDialog"
          @email-bound="userStore.getUserData"
        />

        <!-- Онбординг-тур для новых пользователей (после email-диалога) — только для авторизованных -->
        <onboarding-overlay
          v-if="showOnboarding && authStore.isAuth"
          @complete="showOnboarding = false"
        />
      </template>
    </v-container>
    <app-footer v-if="!authStore.isAuth" />
  </div>
</template>

<style scoped lang="scss">
</style>
